import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Spinner } from "../../../components/Spinner";
import { useGetScanReport } from "../../../helpers/queries/scans/useGetScans";
import Results from "./components/Results";
import Hosts from "./components/Hosts";
import Ports from "./components/Ports";
import Vulnerabilities from "./components/Vulnerabilities";
import OperatingSystems from "./components/OperatingSystems";
import Applications from "./components/Applications";
import { FaArrowLeft } from "react-icons/fa6";
import { IoIosArrowForward } from "react-icons/io";
import { Button } from "../../../components/ui/button";
import { LuDownload } from "react-icons/lu";
import { exportAsCsv, exportAsPdf } from "../../../helpers/api";
import csv from "../../../assets/icons/csv.png";
import pdf from "../../../assets/icons/pdf.png";
import { toast } from "react-toastify";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../components/ui/popover";
import LiveHosts from "./components/LiveHosts";

const Report = () => {
  const location = useLocation();
  const { report_id, target_name, status } = location?.state;
  const [activeTab, setActiveTab] = useState("result");
  const { isLoading, isSuccess, isError, data } = useGetScanReport(report_id);
  const [activeModalId, setActiveModalId] = useState(false);

  const vulnCount = data?.vulnerability_counts;

  const chartData = [
    {
      level: "Total",
      count: vulnCount?.total ?? 0,
      fill: "#828282",
    },
    {
      level: "Critical",
      count: vulnCount?.critical ?? 0,
      fill: "#b22222",
    },
    {
      level: "High",
      count: vulnCount?.high ?? 0,
      fill: "#E3650F",
    },
    {
      level: "Medium",
      count: vulnCount?.medium ?? 0,
      fill: "#c49102",
    },
    {
      level: "Low",
      count: vulnCount?.low ?? 0,
      fill: "#00c851",
    },
  ];

  const tabs = [
    { key: "result", label: "Results" },
    { key: "hosts", label: "Hosts" },
    ...(data?.fqdns && data.fqdns.length > 0
      ? [{ key: "fqdns", label: "Live Hosts" }]
      : []),
    { key: "vul", label: "Vulnerabilities" },
    { key: "ports", label: "Ports" },
    { key: "app", label: "Applications" },
    { key: "ops", label: "Operating Systems" },
  ];

  function getValueByKey(keyName) {
    const result = data?.breakdown?.find((item) => item.key === keyName);
    return result ? result.value : null;
  }

  const [fetchPdfLoading, setFetchPdfLoading] = useState(false);

  const fetchPdf = async (id) => {
    setFetchPdfLoading(true);
    try {
      const file = await exportAsPdf(id);

      if (!(file instanceof Blob)) {
        console.error("Error: Received invalid PDF file", file);
        toast.error("Failed to download");
        return;
      }
      // console.log("PDF Blob:", file);
      const pdfUrl = window.URL.createObjectURL(file);

      const a = document.createElement("a");
      a.href = pdfUrl;
      a.download = `${target_name} report.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (error) {
      console.error("Failed to fetch PDF:", error);
      toast.error("Failed to download");
    } finally {
      setFetchPdfLoading(false);
      setActiveModalId(null); // Close modal
    }
  };

  const [downloadCsvLoading, setDownloadCsvLoading] = useState(false);

  const downloadCsv = async (id) => {
    setDownloadCsvLoading(true);
    try {
      const file = await exportAsCsv(id);

      if (!(file instanceof Blob)) {
        console.error("Error: Received invalid CSV file", file);
        toast.error("Failed to download");
        return;
      }
      const url = window.URL.createObjectURL(file);

      const a = document.createElement("a");
      a.href = url;
      a.download = `${target_name} report.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (error) {
      console.error("Failed to fetch CSV:", error);
      toast.error("Failed to download");
    } finally {
      setDownloadCsvLoading(false);
      setActiveModalId(null); // Close modal
    }
  };

  return (
    <>
      <div className="w-full">
        <div className="w-full lg:block py-4 lg:px-10 md:px-5 px-4 bg-white border-b ">
          <div className="flex items-center gap-2 text-sm">
            <Link to="/dashboard/scan-history">
              <p className="flex items-center gap-2 text-neutral800">
                <span className="p-1 text-neutral900 border border-neutral200 rounded-lg">
                  <FaArrowLeft />
                </span>
                Scans
              </p>
            </Link>
            <IoIosArrowForward />
            <p className="text-[#B7B7B9]">Report for {target_name}</p>
          </div>

          <div className="flex justify-between md:flex-row flex-col gap-4 md:items-center mt-2">
            <p className="text-darkGrey font-semibold text-md">
              Report for {target_name}
            </p>

            <div>
              <Popover
                open={activeModalId}
                onOpenChange={(open) => setActiveModalId(open)}
              >
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="text-neutral900 text-sm font-medium pr-3"
                    onClick={() => {
                      setActiveModalId(true);
                    }}
                    disabled={status === "RUNNING"}
                  >
                    <LuDownload size={18} /> Export report
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-fit p-1">
                  <Button
                    className="py-2 px-3 flex items-center gap-2 rounded-md cursor-pointer"
                    onClick={() => {
                      fetchPdf(report_id);
                    }}
                    variant={"white"}
                    loading={fetchPdfLoading}
                  >
                    <img src={pdf} alt="pdf" className="w-[26px]" />
                    Export as PDF
                  </Button>
                  <Button
                    className="py-2 px-3 flex items-center gap-2 rounded-md cursor-pointer"
                    onClick={() => {
                      downloadCsv(report_id);
                    }}
                    variant={"white"}
                    loading={downloadCsvLoading}
                  >
                    <img src={csv} alt="csv" className="w-[26px]" />
                    Export as CSV
                  </Button>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </div>

        {isLoading && !isSuccess && (
          <div className="h-[55vh] w-full flex items-center justify-center">
            <Spinner />
          </div>
        )}

        {isError && !isLoading && (
          <div className="h-[45vh] w-full flex justify-center flex-col items-center">
            <p className="font-semibold text-xl text-center">
              There was an error getting the data
            </p>
            <p className="text-center">{data?.message ?? ""}</p>
          </div>
        )}

        {!isLoading && isSuccess && data?.breakdown?.length !== 0 && (
          <div className="lg:p-10 md:p-5 p-4 bg-white h-[100%]">
            <div className="w-full">
              <p className="font-semibold text-darkGrey mb-3">
                Vulnerability Count
              </p>
              <div className="flex md:flex-row flex-wrap gap-4 mb-4">
                {chartData.map((entry, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 border-[#cfcfd2] md:pr-4 pr-2"
                  >
                    <div
                      style={{
                        backgroundColor: entry.fill,
                        width: "20px",
                        height: "20px",
                        borderRadius: "50%",
                      }}
                    ></div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">
                        {entry.level}:
                      </span>
                      <span>{entry.count}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="overflow-x-auto">
                <div className="min-w-[1100px] w-full">
                  {/* Tab Buttons */}
                  <div className="w-fit rounded-lg bg-neutral200 h-[36px] flex py-1 relative overflow-hidden mb-4">
                    <div
                      className={`absolute top-1 left-1  h-[calc(100%-8px)] rounded-lg bg-white shadow-scheduleShadow transition-all duration-500`}
                      style={{
                        width: `${99.3 / tabs.length}%`,
                        transform: `translateX(${
                          tabs.findIndex((tab) => tab.key === activeTab) * 100
                        }%)`,
                      }}
                    ></div>

                    {tabs.map((tab) => (
                      <button
                        key={tab.key}
                        className={`w-[150px] relative z-10 text-sm rounded-full font-outfit font-medium transition-colors duration-300 cursor-pointer ${
                          activeTab === tab.key
                            ? "text-neutral900"
                            : "text-neutral700"
                        }`}
                        // onMouseEnter={() => {
                        //   setActiveTab(tab.key);
                        // }}
                        onClick={() => setActiveTab(tab.key)}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {activeTab === "result" && (
                <Results results={getValueByKey("result")} />
              )}
              {activeTab === "hosts" && (
                <Hosts results={getValueByKey("hosts")} />
              )}
              {activeTab === "ports" && (
                <Ports results={getValueByKey("ports")} />
              )}
              {activeTab === "app" && (
                <Applications results={getValueByKey("applications")} />
              )}
              {activeTab === "vul" && (
                <Vulnerabilities results={data?.userVulns} />
              )}
              {activeTab === "ops" && (
                <OperatingSystems results={getValueByKey("operatingSystems")} />
              )}

              {activeTab === "fqdns" && <LiveHosts results={data?.fqdns} />}
            </div>

            {/* <PaginationComponent
                lastPage={data?.databases?.last_page!}
                currentPage={currentPage}
                handlePageChange={updatePage}
              /> */}
          </div>
        )}

        {data?.breakdown?.length === 0 && !isLoading && isSuccess && (
          <div className=" h-[45vh] w-full flex justify-center flex-col items-center text-center">
            <p className=" font-semibold text-xl">There are no results</p>
          </div>
        )}
      </div>
    </>
  );
};

export default Report;
